// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import {
  loadIngestedTransactions,
  exportIngestedTransactionsData,
} from '../../../actions/keyCodeActions';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import FormattedDate from '../../common/FormattedDate';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import TransactionStatusBadge from './TransactionStatusBadge';
import KebabMenu from '../../common/KebabMenu';
import Button from '../../common/Button';
import icons from '../../../constants/icons';
import UserLink from '../../common/links/UserLink';
import { useTranslation } from 'react-i18next';

export interface IngestedBatchesListProps {
  reload?: any;
  onRemove: (e: any, item: any) => void;
  onRetry: (e: any, item: any) => void;
  setBreadcrumb: (crumb: any) => void;
}

function IngestedBatchesList({ reload, onRemove, onRetry, setBreadcrumb }: IngestedBatchesListProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const defaultVisibleColumns = [
    'uploadedDate',
    'gameName',
    'keyWorkflowName',
    'requestReference',
    'tag1',
    'keyCount',
    'manualImportUser',
    'status',
  ];
  const allColumns = [...defaultVisibleColumns, 'packageId'];
  const suffixColumns = ['id'];
  const [transactions, setTransactions] = useState<any>();
  const params = new URLSearchParams(location.search);
  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'ingestedbatcheslist-settings',
      getTableSettingsDefaults({
        defaultSort: 'uploadedDate',
        direction: 'desc',
        defaultVisibleColumns,
      }),
      null,
      loadIngestedTransactions,
      setTransactions,
      reload,
      t('Failed to load ingested transactions')
    );

  const getToolTip = (transaction: any) => {
    if (!transaction?.keysDirty) return '';
    if (transaction.requestId != null) return t('Already assigned to a request');
    if (transaction.keysDirty) return t('Keys have already been allocated');
    if (transaction.codesImported !== true) return t('Keys are being processed and cannot be stopped');
    return '';
  };

  let columnsConfig = [
    {
      Key: 'uploadedDate',
      Label: t('Uploaded date'),
      width: '10%',
      Render: (item: any) => (
        <Link to={'/keys/ingested-batches/' + item.id}>
          <FormattedDate value={item.uploadedDate} variant="default" />
        </Link>
      ),
    },
    {
      Key: 'gameName',
      Label: t('Game'),
      width: '15%',
      Render: (item: any) => (
        <>
          <NavLink to={`/sku/${item.gameSkuId}`} className="game-title">
            {item.gameName}
          </NavLink>
          <br />
          <NavLink to={`/sku/${item.gameSkuId}`}>{item.gameSkuName}</NavLink>
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyWorkflowName',
      Label: t('Key pool'),
      width: '10%',
      Render: (item: any) =>
        item.keyWorkflowId ? (
          <Link to={'/settings/workflow/' + item.keyWorkflowId}>
            {item.keyWorkflowName}
          </Link>
        ) : (
          '--'
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'requestReference',
      Label: t('Request'),
      width: '10%',
      Render: (item: any) =>
        item.requestGroupId ? (
          <Link to={'/request/' + item.requestGroupId}>
            {item.requestReference}
          </Link>
        ) : (
          ''
        ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'tag1',
      Label: t('Tag'),
      width: '20%',
      Render: (item: any) => item.tag1,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyCount',
      Label: t('Key count'),
      width: '5%',
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.keyCount >= 0 ? CommaNumber_NoDecimal(item.keyCount) : '--',
    },
    {
      Key: 'manualImportUser',
      Label: t('Uploaded by'),
      width: '10%',
      Render: (item: any) => (
        <>
          <UserLink id={item.manualImportUserId} name={item.manualImportUser} />
          <br />
          {item.manualImportUserCompany}
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'status',
      Label: t('Status'),
      width: '10%',
      Render: (item: any) => (
        <TransactionStatusBadge
          success={item.success}
          partialSuccess={item.partialSuccess}
          message={item.message}
          status={item.status}
        />
      ),
      Filter: {
        Render: (col: any) => {
          const value = col.filterValue ?? params.get(`f_${col.Key}`) ?? '';
          return (
            <select
              className={`form-control ${value ? 'filtered' : ''}`}
              value={value}
              onChange={(el) => filterChange(col.Key, (el.target as HTMLSelectElement).value)}
            >
              <option value="">{t('All')}</option>
              <option value="failed">{t('Failed')}</option>
              <option value="pending">{t('Pending')}</option>
              <option value="success">{t('Success')}</option>
            </select>
          );
        },
      },
    },
    {
      Key: 'id',
      Label: '',
      className: 'text-right no-navigate no-wrap',
      width: '7%',
      Sort: false,
      Render: (item: any) => (
        <KebabMenu
          take={1}
          className="inline-icons"
          buttons={[
            <Button
              key="remove"
              className="btn btn-default me-2"
              isDisabled={item.keysDirty}
              title={getToolTip(item)}
              onClick={(e) => onRemove(e, item)}
              icon={icons.DELETE}
              text={t('Remove')}
            />,
            <Button
              key="retry"
              className="btn btn-default me-2"
              isDisabled={item.keysDirty}
              title={getToolTip(item)}
              onClick={(e) => onRetry(e, item)}
              icon={icons.RETRY}
              text={t('Retry')}
            />,
          ]}
        />
      ),
    },
    {
      Key: 'packageId',
      Label: t('Package ID'),
      width: '20%',
      Render: (item: any) => item.packageId,
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const suffixColumnsWithConfig = getVisibleColumns(suffixColumns, columnsConfig);
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return transactions ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        suffixColumns={suffixColumnsWithConfig}
        allColumns={allColumns}
        rows={transactions.data.map((x: any) => ({ Key: x.id, ...x }))}
        totalRowCount={transactions.totalRowCount}
        filteredRowCount={transactions.filteredRowCount}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportIngestedTransactionsData}
        scopes={transactions.scopes}
        setBreadcrumb={setBreadcrumb}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default IngestedBatchesList;
